// src/components/WhatsAppTestDialog/WhatsAppTestDialog.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Backdrop,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

export default function WhatsAppTestDialog({
  open,
  template = {},
  onClose,
  onSend,        // (recipients: Array<{ number: string, firstName: string }>) => Promise<SendResult[]>
  onMarkSuccess, // () => Promise<void>
}) {
  // --- initial state factory (so we can reuse it) ---
  const makeInitialEntries = () => [{ countryCode: "", number: "", firstName: "" }];

  // --- hooks at top ---
  const [entries, setEntries] = useState(makeInitialEntries());
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  // --- whenever `open` goes from true → false, reset all local state ---
  useEffect(() => {
    if (!open) {
      setEntries(makeInitialEntries());
      setSending(false);
      setResults(null);
    }
  }, [open]);

  // if closed, render nothing
  if (!open) return null;

  const addField = () =>
    entries.length < 10 &&
    setEntries([
      ...entries,
      { countryCode: "", number: "", firstName: "" },
    ]);

  const removeField = (i) =>
    setEntries(entries.filter((_, idx) => idx !== i));

  const changeField = (i, key, val) =>
    setEntries(
      entries.map((e, idx) =>
        idx === i ? { ...e, [key]: val } : e
      )
    );

const handleSend = async () => {
  setSending(true);
  try {
    const recips = entries
      .map(e => ({ number: e.countryCode + e.number, firstName: e.firstName }))
      .filter(r => r.number);
    const res = await onSend(recips);
    setResults(res);
  } catch (err) {
    // put the single error into your results list
    setResults([{ number: null, success: false, error: err.message }]);
  } finally {
    setSending(false);
  }
};

  return (
    <>
      <Dialog open={open} fullWidth onClose={() => !sending && onClose()}>
        <DialogTitle>
          Test Template "{template.template_name || ""}"
        </DialogTitle>

        <DialogContent dividers>
          <Typography gutterBottom>
            Enter up to 10 recipients:
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            {entries.map((e, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1 }}>
                <TextField
                  label="Country code"
                  value="+44"
                  InputProps={{ readOnly: true }}
                  disabled={sending || !!results}
                  sx={{ width: 100 }}
                />
                <TextField
                  label="Number"
                  placeholder="UK WhatsApp number"
                  value={e.number}
                  onChange={evt => changeField(i, "number", evt.target.value)}
                  disabled={sending || !!results}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="First name"
                  placeholder="Snaggler"
                  value={e.firstName}
                  onChange={evt => changeField(i, "firstName", evt.target.value)}
                  disabled={sending || !!results}
                  sx={{ width: 150 }}
                />
                {!results && (
                  <Button onClick={() => removeField(i)} disabled={sending}>
                    ×
                  </Button>
                )}
              </Box>
            ))}
            {!results && entries.length < 10 && (
              <Button onClick={addField} disabled={sending}>
                + Add Recipient
              </Button>
            )}
          </Box>

          {results && (
            <>
              <Typography variant="subtitle1">Send results:</Typography>
              <List>
                {results.map((r, i) => (
                  <ListItem key={i}>
                    <ListItemText
                      primary={`${r.number}: ${r.success ? "✅" : "❌"}`}
                      secondary={r.message || r.error}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>

        <DialogActions>
          {!results ? (
            <Button
              onClick={handleSend}
              variant="contained"
              disabled={sending}
            >
              Send
            </Button>
          ) : (
            <Button
              onClick={async () => {
                await onMarkSuccess();
                onClose();
              }}
              variant="contained"
            >
              Test Successful?
            </Button>
          )}
          <Button onClick={onClose} disabled={sending}>
            {results ? "Close" : "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={sending} sx={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
